import React from 'react'

import {
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  ResponsiveValue,
} from '@gousto-internal/citrus-react'
import type { RecipeGridContentBlock, StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { RecipeCount } from '../CollectionScreen/RecipeCount'
import { RecipeTile } from './RecipeTile'
import type { RecipeTileCustomisationData } from './RecipeTile/RecipeTile.types'

type RecipeGridTileState = {
  isInBasket: boolean
  canAddRecipes: boolean
  reachedLimit: boolean
}

type RecipeGridTileAction = (recipeId: RecipeUUID, recipePosition: number) => void

export type SupplementaryItem = {
  id: string
  position: number | 'end'
  content: React.ReactNode
}

const idleCustomisationData: RecipeTileCustomisationData = {
  isLoadingCustomisations: false,
  hasCustomisationError: false,
  hasOpenedCustomisations: false,
}

export type RecipeGridProps = {
  contentBlock: RecipeGridContentBlock
  recipeCountTextColor?: string
  supplementaryItems?: SupplementaryItem[]
  getRecipeTileState: (recipeId: RecipeUUID) => RecipeGridTileState
  getRecipeCustomisationData?: (recipeId: RecipeUUID) => RecipeTileCustomisationData
  customisationEducationRecipeId?: RecipeUUID | null
  onRecipeClick: RecipeGridTileAction
  onAddClick: RecipeGridTileAction
  onRemoveClick: RecipeGridTileAction
  onBlockedAddAttempt: () => void
  onCustomiseOpen?: RecipeGridTileAction
  onCustomiseClose?: () => void
  onCustomisationEducationClose?: () => void
}

const recipeGridItemWidth: ResponsiveValue<string> = [
  '100%',
  '100%',
  'calc((100% - 16px) / 2)',
  'calc((100% - 32px) / 3)',
]

const isStandardRecipeItem = (item: StandardRecipeItem): item is StandardRecipeItem =>
  item.type === 'standard-recipe'

type GridItemProps = {
  children: React.ReactNode
}

const GridItem = ({ children }: GridItemProps) => (
  <Box
    display={Display.Flex}
    flexBasis={recipeGridItemWidth}
    flexGrow={0}
    flexShrink={0}
    maxWidth={recipeGridItemWidth}
  >
    {children}
  </Box>
)

export const RecipeGrid = ({
  contentBlock,
  recipeCountTextColor,
  supplementaryItems = [],
  getRecipeTileState,
  getRecipeCustomisationData,
  customisationEducationRecipeId,
  onRecipeClick,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  onCustomiseOpen,
  onCustomiseClose,
  onCustomisationEducationClose,
}: RecipeGridProps) => {
  const recipes = contentBlock.items.filter(isStandardRecipeItem)
  const numericSupplementaryItems = supplementaryItems.filter(
    (item): item is SupplementaryItem & { position: number } =>
      typeof item.position === 'number' && item.position > 0 && item.position < recipes.length,
  )
  const endSupplementaryItems = supplementaryItems.filter(({ position }) => position === 'end')

  const renderSupplementaryItems = (items: SupplementaryItem[]) =>
    items.map(({ id, content }) => <GridItem key={id}>{content}</GridItem>)

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      data-testid="recipe-grid"
    >
      <Box paddingH={[3, 3, 0]}>
        <RecipeCount count={contentBlock.recipeCount} textColor={recipeCountTextColor} />
      </Box>
      <Box
        display={Display.Flex}
        wrap
        justifyContent={JustifyContent.Center}
        gap={[3, 3, 4]}
        paddingH={[3, 3, 0]}
        data-testid="recipe-grid-items"
      >
        {recipes.map((item, index) => {
          const recipePosition = index + 1
          const recipeTileState = getRecipeTileState(item.id)
          const customisationData = getRecipeCustomisationData?.(item.id) ?? idleCustomisationData

          return (
            <React.Fragment key={item.id}>
              <GridItem>
                <RecipeTile
                  recipe={item}
                  isInBasket={recipeTileState.isInBasket}
                  canAddRecipes={recipeTileState.canAddRecipes}
                  reachedLimit={recipeTileState.reachedLimit}
                  onClick={(recipeId) => onRecipeClick(recipeId, recipePosition)}
                  onAddClick={(recipeId) => onAddClick(recipeId, recipePosition)}
                  onRemoveClick={(recipeId) => onRemoveClick(recipeId, recipePosition)}
                  onBlockedAddAttempt={onBlockedAddAttempt}
                  customisationData={customisationData}
                  onCustomiseOpen={(recipeId) => onCustomiseOpen?.(recipeId, recipePosition)}
                  onCustomiseClose={onCustomiseClose}
                  showCustomisationEducation={item.id === customisationEducationRecipeId}
                  onCustomisationEducationClose={onCustomisationEducationClose}
                />
              </GridItem>
              {renderSupplementaryItems(
                numericSupplementaryItems.filter(({ position }) => position === recipePosition),
              )}
            </React.Fragment>
          )
        })}
        {renderSupplementaryItems(endSupplementaryItems)}
      </Box>
    </Box>
  )
}
